// Shared, pure zone-document derivations used by BOTH the vendor Documents tab
// (manage-account) and the internal Users console (manage-users) via the shared
// zone-documents components. Lifting these out of manage-account guarantees both
// surfaces compute requirement rows, covering documents, aggregate state and the
// per-document cue identically — the single source of truth the redesign relies
// on. The vendor-facing aggregate/cue helpers never surface the approve/reject
// decision; the internal-only review helpers (reviewStatusOf / docReviewState)
// are consumed only when a component is rendered in internal mode.

export type NodeAggregateState = 'required' | 'under-review' | 'satisfied';
export type DocCueKind = 'satisfied' | 'review' | 'attention';
export type DocReviewState = 'pending' | 'approved' | 'rejected' | 'expired';

export interface ZoneRequiredNode {
  service: any;
  subType: any | null;
  label: string;
}

export type ZoneRequirementScope = 'NATIONAL' | 'STATE_ALL' | 'STATE_SPECIFIC';

// A license/certification definition as returned by the PA-facing
// GET /license-requirements endpoint (already filtered to the enabled
// requirements applicable to the caller's offered services).
export interface LicenseRequirement {
  id: string;
  documentTitle: string;
  zoneRequirement: ZoneRequirementScope;
  states: string[];
  issueDateRequired: boolean;
  expirationDateRequired: boolean;
  associations: {
    serviceTypeId: string;
    serviceSubTypeType: string | null;
  }[];
  enabled?: boolean;
}

// One named requirement row for the vendor Documents tab: the service (and
// optional sub-service) the row is scoped to under a zone, plus the license that
// names it. `label` is the license's documentTitle; `serviceContext` names the
// node it applies to; `zoneContext` describes the credential's geographic scope.
export interface LicenseRow {
  service: any;
  subType: any | null;
  license: LicenseRequirement;
  label: string;
  serviceContext: string;
  zoneContext: string;
}

// The enabled services of a zone (the only ones the vendor selected for it).
export function filterServiceList(services: any): any[] {
  return (services ?? []).filter((service: any) => service?.enabledForZone);
}

// The enabled sub-services of a service that themselves require a document, per
// the vendorDocumentRequired boolean. INTERNAL surface only — the vendor surface
// is license-row-driven and no longer reads vendorDocumentRequired.
export function requiredSubTypes(service: any): any[] {
  return (service?.serviceSubTypes ?? []).filter(
    (st: any) => st?.enabledForZone && st?.vendorDocumentRequired,
  );
}

// A document covers a node when it is filed under the node's service. For a
// sub-service node the document must additionally blanket that sub-service key;
// a service-level node (no sub-type) is covered by any document filed under it.
function docCoversNode(
  zoneDocument: any,
  serviceTypeKey: string,
  subTypeKey?: string | null,
): boolean {
  if (!zoneDocument || zoneDocument.serviceType !== serviceTypeKey) {
    return false;
  }
  if (!subTypeKey) {
    return true;
  }
  return (zoneDocument.serviceSubTypes ?? []).includes(subTypeKey);
}

// All zone documents whose coverage includes the given node. A service/sub-
// service can have MANY documents linked to it, so this returns the full list
// (no single "winner").
export function coveringDocuments(
  serviceZone: any,
  serviceTypeKey: string,
  subTypeKey?: string | null,
): any[] {
  return (serviceZone?.zoneDocuments ?? []).filter((doc: any) =>
    docCoversNode(doc, serviceTypeKey, subTypeKey),
  );
}

// Whether a covering document's expiration date is in the past.
export function isExpired(zoneDocument: any): boolean {
  if (!zoneDocument?.expirationDate) {
    return false;
  }
  const expires = new Date(zoneDocument.expirationDate).getTime();
  return !Number.isNaN(expires) && expires < Date.now();
}

// Flattened list of the document-requiring nodes in a zone: one entry per
// service-level requirement and per required sub-service. Drives the Approach A
// requirement rows and the zone summary line.
export function zoneRequiredNodes(serviceZone: any): ZoneRequiredNode[] {
  const nodes: ZoneRequiredNode[] = [];
  for (const service of filterServiceList(serviceZone?.services)) {
    if (service.vendorDocumentRequired) {
      nodes.push({ service, subType: null, label: service.displayName });
    }
    for (const subType of requiredSubTypes(service)) {
      nodes.push({
        service,
        subType,
        label: service.displayName + ' — ' + subType.displayName,
      });
    }
  }
  return nodes;
}

// A service and its document-requiring nodes, grouped for the internal nested
// layout. `serviceNode` is the service-level requirement row (present only when
// the service itself requires a document); `subNodes` are the required
// sub-service rows, labelled by the sub-service alone (the parent service is
// implied by the group they render under). `groupLabel` heads the bordered
// sub-service group. Consumed ONLY by the internal (review) surface — the vendor
// surface keeps the flat `zoneRequiredNodes` list ("Service — Subtype").
export interface ZoneServiceGroup {
  service: any;
  serviceNode: ZoneRequiredNode | null;
  subNodes: ZoneRequiredNode[];
  groupLabel: string;
}

export function zoneServiceGroups(serviceZone: any): ZoneServiceGroup[] {
  const groups: ZoneServiceGroup[] = [];
  for (const service of filterServiceList(serviceZone?.services)) {
    const serviceNode: ZoneRequiredNode | null = service.vendorDocumentRequired
      ? { service, subType: null, label: service.displayName }
      : null;
    const subNodes: ZoneRequiredNode[] = requiredSubTypes(service).map(
      (subType: any) => ({
        service,
        subType,
        label: subType.displayName,
      }),
    );
    if (serviceNode || subNodes.length > 0) {
      groups.push({
        service,
        serviceNode,
        subNodes,
        groupLabel: service.displayName + ' · sub-service licenses',
      });
    }
  }
  return groups;
}

// "Additional documents — not tied to a service": zone documents that cover no
// displayed required node (legacy docs with no serviceType, or docs whose
// association no longer maps to a required node). Association stays optional.
export function additionalZoneDocuments(serviceZone: any): any[] {
  const covered = new Set<any>();
  for (const node of zoneRequiredNodes(serviceZone)) {
    for (const doc of coveringDocuments(
      serviceZone,
      node.service.type,
      node.subType ? node.subType.type : null,
    )) {
      covered.add(doc);
    }
  }
  return (serviceZone?.zoneDocuments ?? []).filter(
    (doc: any) => !covered.has(doc),
  );
}

// Aggregate vendor-facing state for a node, computed over ALL its covering
// documents. Three outcomes, never the approve/reject decision: 'satisfied' if
// ANY covering doc is APPROVED and not expired; else 'under-review' if ANY is
// PENDING; else 'required'. The word "rejected" is never surfaced.
export function nodeAggregateState(
  serviceZone: any,
  serviceTypeKey: string,
  subTypeKey?: string | null,
): NodeAggregateState {
  const docs = coveringDocuments(serviceZone, serviceTypeKey, subTypeKey);
  let anyPending = false;
  for (const doc of docs) {
    const status = (doc?.reviewStatus || '').toUpperCase();
    if (status === 'APPROVED' && !isExpired(doc)) {
      return 'satisfied';
    }
    if (status === 'PENDING') {
      anyPending = true;
    }
  }
  return anyPending ? 'under-review' : 'required';
}

// Per-document vendor-facing cue. Reflects that one document's own state, never
// the approve/reject decision: an APPROVED-and-valid doc is "On file"; a PENDING
// doc is "Under review"; an expired doc is "Expired — re-upload"; anything else
// (rejected / unknown) collapses to the neutral "Re-upload needed".
export function docCue(zoneDocument: any): string {
  const status = (zoneDocument?.reviewStatus || '').toUpperCase();
  const expired = isExpired(zoneDocument);
  if (status === 'APPROVED' && !expired) {
    return 'On file';
  }
  if (status === 'PENDING' && !expired) {
    return 'Under review';
  }
  if (expired) {
    return 'Expired — re-upload';
  }
  return 'Re-upload needed';
}

// Cue category behind docCue(), used only to pick the cue colour class
// (satisfied = green, review = amber, attention = red). Never rendered as text.
export function docCueKind(zoneDocument: any): DocCueKind {
  const status = (zoneDocument?.reviewStatus || '').toUpperCase();
  const expired = isExpired(zoneDocument);
  if (status === 'APPROVED' && !expired) {
    return 'satisfied';
  }
  if (status === 'PENDING' && !expired) {
    return 'review';
  }
  return 'attention';
}

// Zone summary: how many required nodes are still outstanding — no valid
// (approved, non-expired) document and none pending review.
export function zoneRequiredCount(serviceZone: any): number {
  return zoneRequiredNodes(serviceZone).filter(
    (node) =>
      nodeAggregateState(
        serviceZone,
        node.service.type,
        node.subType ? node.subType.type : null,
      ) === 'required',
  ).length;
}

// Zone summary: how many required nodes currently have a document under review.
export function zoneUnderReviewCount(serviceZone: any): number {
  return zoneRequiredNodes(serviceZone).filter(
    (node) =>
      nodeAggregateState(
        serviceZone,
        node.service.type,
        node.subType ? node.subType.type : null,
      ) === 'under-review',
  ).length;
}

// ── Internal-only review helpers (consumed only in internal mode) ────────────

// The document's raw review decision, normalized. Drives which review actions
// a card offers: APPROVED → Revoke; REJECTED → Re-approve; PENDING → Approve /
// Reject. Independent of expiration (an expired-but-approved doc is still an
// APPROVED decision the reviewer may revoke).
export function reviewStatusOf(
  zoneDocument: any,
): 'PENDING' | 'APPROVED' | 'REJECTED' {
  const status = (zoneDocument?.reviewStatus || '').toUpperCase();
  if (status === 'APPROVED') {
    return 'APPROVED';
  }
  if (status === 'REJECTED') {
    return 'REJECTED';
  }
  return 'PENDING';
}

// The internal-only per-document state pill value. REJECTED is reported ahead of
// EXPIRED (the rejection is the actionable fact); an expired-but-approved doc
// shows EXPIRED; a valid approved doc shows APPROVED; otherwise PENDING.
export function docReviewState(zoneDocument: any): DocReviewState {
  const status = reviewStatusOf(zoneDocument);
  if (status === 'REJECTED') {
    return 'rejected';
  }
  if (isExpired(zoneDocument)) {
    return 'expired';
  }
  if (status === 'APPROVED') {
    return 'approved';
  }
  return 'pending';
}

// Human label for a review state pill.
export function reviewStateLabel(state: DocReviewState): string {
  switch (state) {
    case 'approved':
      return 'Approved';
    case 'rejected':
      return 'Rejected';
    case 'expired':
      return 'Expired';
    default:
      return 'Pending';
  }
}

// ── License-requirement-driven derivations (vendor Documents tab) ─────────────
// These replace the vendorDocumentRequired boolean as the source of requirement
// rows on the VENDOR surface. Rows come from the applicable license requirements
// (GET /license-requirements) rather than a per-node boolean, and per-license
// state keys on each document's licenseRequirementId. The internal (review)
// surface keeps using the vendorDocumentRequired-based helpers above until it is
// migrated separately.

// A normalized id compare (licenseRequirementId is a UUID string over the wire;
// tolerate case + object identity differences).
function sameLicenseId(a: any, b: any): boolean {
  if (a == null || b == null) {
    return false;
  }
  return String(a).toLowerCase() === String(b).toLowerCase();
}

// The human-readable geographic scope of a credential, for the row's context
// label. STATE_SPECIFIC lists the states it is issued in.
export function zoneRequirementLabel(license: LicenseRequirement): string {
  switch (license?.zoneRequirement) {
    case 'NATIONAL':
      return 'Nationally issued';
    case 'STATE_ALL':
      return 'State issued — all states';
    case 'STATE_SPECIFIC': {
      const states = (license?.states ?? []).filter((s) => !!s);
      return states.length > 0
        ? 'State issued — ' + states.join(', ')
        : 'State issued';
    }
    default:
      return '';
  }
}

// Whether a license surfaces as a required row within a given zone. STATED,
// REFINABLE ASSUMPTION (flag for Product): a National or State-All credential is
// prompted in every zone where the associated service is offered; a
// State-Specific credential is prompted only in zones whose state is one of the
// license's states. A nationwide zone carries no single state, so a
// State-Specific credential does not surface there (there is no state to match).
// "National once" (dedupe a national credential to a single zone) is deferred —
// the per-zone layout prompts it in each applicable zone and any one upload
// claims coverage via the existing zone-document coverage model.
export function licenseAppliesToZone(
  license: LicenseRequirement,
  serviceZone: any,
): boolean {
  if (!license) {
    return false;
  }
  if (license.zoneRequirement === 'STATE_SPECIFIC') {
    if (serviceZone?.nationwide) {
      return false;
    }
    return (license.states ?? []).some(
      (state) => state === serviceZone?.state,
    );
  }
  // NATIONAL / STATE_ALL (and any unknown scope): applies wherever the service is.
  return true;
}

// The enabled sub-service of a service matching a sub-type `type`, or null. Used
// to place a sub-type-level license row only when the vendor actually offers that
// sub-service in the zone.
function enabledSubTypeByType(service: any, subTypeType: string): any {
  return (
    (service?.serviceSubTypes ?? []).find(
      (st: any) => st?.enabledForZone && st?.type === subTypeType,
    ) ?? null
  );
}

// Builds the named requirement rows for one service zone from the applicable
// license requirements. Each association that resolves (via serviceTypeId → the
// service `type`, bridged by `serviceTypeIdToType`) to an enabled service in the
// zone yields a row: a service-level association (null serviceSubTypeType) makes
// a service row; a sub-type association makes a sub-type row when the vendor
// offers that sub-service. A node can yield MULTIPLE rows (one per attached
// license); the service-row → sub-type-row distinction is preserved. Rows are
// de-duplicated on service+sub-type+license so a license attached twice cannot
// double-render.
export function zoneLicenseRows(
  serviceZone: any,
  licenses: LicenseRequirement[] | null | undefined,
  serviceTypeIdToType: Record<string, string> | null | undefined,
): LicenseRow[] {
  const rows: LicenseRow[] = [];
  const seen = new Set<string>();
  const idToType = serviceTypeIdToType ?? {};
  const applicable = (licenses ?? []).filter((license) =>
    licenseAppliesToZone(license, serviceZone),
  );
  for (const service of filterServiceList(serviceZone?.services)) {
    for (const license of applicable) {
      for (const association of license.associations ?? []) {
        const associatedType = idToType[association?.serviceTypeId];
        if (!associatedType || associatedType !== service.type) {
          continue;
        }
        let subType: any | null = null;
        if (association.serviceSubTypeType) {
          subType = enabledSubTypeByType(service, association.serviceSubTypeType);
          if (!subType) {
            // The vendor does not offer this sub-service in this zone.
            continue;
          }
        }
        const key =
          service.type +
          '|' +
          (subType ? subType.type : '') +
          '|' +
          license.id;
        if (seen.has(key)) {
          continue;
        }
        seen.add(key);
        rows.push({
          service,
          subType,
          license,
          label: license.documentTitle,
          serviceContext: subType
            ? service.displayName + ' — ' + subType.displayName
            : service.displayName,
          zoneContext: zoneRequirementLabel(license),
        });
      }
    }
  }
  return rows;
}

// All zone documents that cover the node AND are tagged with the given license
// id. This is the per-license extension of coveringDocuments: a document
// satisfies a license row only when it is both filed under the node and linked
// to that named requirement.
export function coveringDocumentsForLicense(
  serviceZone: any,
  serviceTypeKey: string,
  subTypeKey: string | null | undefined,
  licenseId: string,
): any[] {
  return coveringDocuments(serviceZone, serviceTypeKey, subTypeKey).filter(
    (doc: any) => sameLicenseId(doc?.licenseRequirementId, licenseId),
  );
}

// Aggregate vendor-facing state for a license row, over its license-matched
// covering documents. Same three outcomes as nodeAggregateState (never the
// approve/reject decision).
export function nodeAggregateStateForLicense(
  serviceZone: any,
  serviceTypeKey: string,
  subTypeKey: string | null | undefined,
  licenseId: string,
): NodeAggregateState {
  const docs = coveringDocumentsForLicense(
    serviceZone,
    serviceTypeKey,
    subTypeKey,
    licenseId,
  );
  let anyPending = false;
  for (const doc of docs) {
    const status = (doc?.reviewStatus || '').toUpperCase();
    if (status === 'APPROVED' && !isExpired(doc)) {
      return 'satisfied';
    }
    if (status === 'PENDING') {
      anyPending = true;
    }
  }
  return anyPending ? 'under-review' : 'required';
}

// Aggregate state for a whole license row (convenience over the keyed helper).
export function licenseRowState(
  serviceZone: any,
  row: LicenseRow,
): NodeAggregateState {
  return nodeAggregateStateForLicense(
    serviceZone,
    row.service.type,
    row.subType ? row.subType.type : null,
    row.license.id,
  );
}

// A service and its license-driven requirement rows, grouped for the internal
// nested layout — the license-row analogue of `zoneServiceGroups`. `serviceRows`
// are the service-level license rows (subType null); `subRows` are the
// sub-service license rows, both sourced from `zoneLicenseRows`. `groupLabel`
// heads the bordered sub-service group. Consumed ONLY by the internal (review)
// surface — the vendor surface keeps the flat `zoneLicenseRows` list.
export interface ZoneLicenseServiceGroup {
  service: any;
  serviceRows: LicenseRow[];
  subRows: LicenseRow[];
  groupLabel: string;
}

export function zoneLicenseServiceGroups(
  serviceZone: any,
  licenses: LicenseRequirement[] | null | undefined,
  serviceTypeIdToType: Record<string, string> | null | undefined,
): ZoneLicenseServiceGroup[] {
  const rows = zoneLicenseRows(serviceZone, licenses, serviceTypeIdToType);
  const groups: ZoneLicenseServiceGroup[] = [];
  // Iterate the SAME enabled-service list zoneLicenseRows keyed its rows on, so a
  // row's `service` matches by object identity (not just value).
  for (const service of filterServiceList(serviceZone?.services)) {
    const serviceRows = rows.filter(
      (row) => row.service === service && !row.subType,
    );
    const subRows = rows.filter(
      (row) => row.service === service && !!row.subType,
    );
    if (serviceRows.length > 0 || subRows.length > 0) {
      groups.push({
        service,
        serviceRows,
        subRows,
        groupLabel: service.displayName + ' · sub-service licenses',
      });
    }
  }
  return groups;
}

// Whether a zone has any applicable license rows to render (vendor surface).
export function zoneHasLicenseRows(
  serviceZone: any,
  licenses: LicenseRequirement[] | null | undefined,
  serviceTypeIdToType: Record<string, string> | null | undefined,
): boolean {
  return zoneLicenseRows(serviceZone, licenses, serviceTypeIdToType).length > 0;
}

// "Additional documents — not tied to a requirement" (Decision 10): zone
// documents not claimed by any displayed license row. This covers pre-existing
// documents with a null licenseRequirementId (strict clean cutover — never
// auto-attributed to a license) as well as documents whose license/coverage no
// longer maps to a rendered row. They stay preserved, downloadable, with their
// review status shown, and can be re-linked to a requirement via the edit dialog.
export function additionalLicenseDocuments(
  serviceZone: any,
  licenses: LicenseRequirement[] | null | undefined,
  serviceTypeIdToType: Record<string, string> | null | undefined,
): any[] {
  const covered = new Set<any>();
  for (const row of zoneLicenseRows(serviceZone, licenses, serviceTypeIdToType)) {
    for (const doc of coveringDocumentsForLicense(
      serviceZone,
      row.service.type,
      row.subType ? row.subType.type : null,
      row.license.id,
    )) {
      covered.add(doc);
    }
  }
  return (serviceZone?.zoneDocuments ?? []).filter(
    (doc: any) => !covered.has(doc),
  );
}

// A single coverage node a license produces in a zone: the service it is filed
// under, plus the optional sub-service the node is scoped to (null for a
// service-level node). The edit/link dialog derives a document's coverage from
// the license's node(s) rather than a free service dropdown.
export interface LicenseZoneNode {
  service: any;
  subType: any | null;
}

// The distinct coverage nodes a given license produces in a zone — its rows from
// `zoneLicenseRows`, reduced to their { service, subType } node. The edit/link
// dialog uses this to derive coverage from the chosen requirement: exactly one
// node auto-scopes the document (no picker); more than one drives the scoped
// "Applies to" picker. Empty when the license is null or produces no row here.
export function licenseNodesInZone(
  serviceZone: any,
  licenses: LicenseRequirement[] | null | undefined,
  serviceTypeIdToType: Record<string, string> | null | undefined,
  licenseId: string | null | undefined,
): LicenseZoneNode[] {
  if (!licenseId) {
    return [];
  }
  return zoneLicenseRows(serviceZone, licenses, serviceTypeIdToType)
    .filter((row) => sameLicenseId(row.license?.id, licenseId))
    .map((row) => ({ service: row.service, subType: row.subType }));
}

// The requirement's coverage nodes under a SINGLE triggering service in a zone —
// `licenseNodesInZone` narrowed to `service`. Sources the upload dialog's
// "also apply to sub-services" checkboxes for a license upload: the sub-service
// nodes (subType non-null) are the requirement's CONFIGURED sub-services under
// that service, offered as default-checked siblings (the service-level node,
// subType null, is the always-covered read-only context). Empty when the license
// is null, the service is null, or the requirement produces no node under that
// service.
export function licenseNodesUnderService(
  serviceZone: any,
  licenses: LicenseRequirement[] | null | undefined,
  serviceTypeIdToType: Record<string, string> | null | undefined,
  licenseId: string | null | undefined,
  service: any,
): LicenseZoneNode[] {
  if (!service) {
    return [];
  }
  return licenseNodesInZone(
    serviceZone,
    licenses,
    serviceTypeIdToType,
    licenseId,
  ).filter((node) => node.service?.type === service.type);
}

// The distinct licenses that produce at least one row in the zone — the offered
// requirement set for the edit/link dialog's "Applies to requirement" selector.
// Sourced from `zoneLicenseRows` (not `applicableLicenses` filtered by
// `licenseAppliesToZone` alone) so every offered requirement derives >= 1 node.
// De-duplicated by license id, row order preserved.
export function offeredZoneLicenses(
  serviceZone: any,
  licenses: LicenseRequirement[] | null | undefined,
  serviceTypeIdToType: Record<string, string> | null | undefined,
): LicenseRequirement[] {
  const result: LicenseRequirement[] = [];
  const seen = new Set<string>();
  for (const row of zoneLicenseRows(serviceZone, licenses, serviceTypeIdToType)) {
    const id = String(row.license?.id ?? '');
    if (!seen.has(id)) {
      seen.add(id);
      result.push(row.license);
    }
  }
  return result;
}

// Vendor zone summary: how many license rows are still outstanding (no valid,
// non-expired document and none pending review).
export function zoneLicenseRequiredCount(
  serviceZone: any,
  licenses: LicenseRequirement[] | null | undefined,
  serviceTypeIdToType: Record<string, string> | null | undefined,
): number {
  return zoneLicenseRows(serviceZone, licenses, serviceTypeIdToType).filter(
    (row) => licenseRowState(serviceZone, row) === 'required',
  ).length;
}

// Vendor zone summary: how many license rows currently have a document under
// review.
export function zoneLicenseUnderReviewCount(
  serviceZone: any,
  licenses: LicenseRequirement[] | null | undefined,
  serviceTypeIdToType: Record<string, string> | null | undefined,
): number {
  return zoneLicenseRows(serviceZone, licenses, serviceTypeIdToType).filter(
    (row) => licenseRowState(serviceZone, row) === 'under-review',
  ).length;
}
